import { toast } from "react-toastify";
import { getOnPropertyChangeTriggeredByDataAsync } from "atoms/fnolInstanceState.atom";
import * as ApiLocations from "api-calls/ApiLocations";
import { apiPost } from "api-calls/ApiCommands";
import * as FieldMappingConstants from "constants/FieldMappingConstants";
import ErrorToastContent from "components/modals/ErrorToastContent";
import {
  ChangeDetails,
  ChangeFieldDetails,
  generateAutoPopulatedByStringForMultiDropdownField,
  MultiDropdownOnPropertyChangeResponse,
  OnPropertyChangeResponse,
  TriggeredByDetails,
} from "shared/OnPropertyChangeObjects";
import * as fnolOnPropertyChangeConstants from "constants/FnolOnPropertyChangeConstants";
import { updateOnPropertyChangeData, updateTPOnPropertyChangeData } from "slices/fnolOnPropertyChangeSlice";
import { FNOLCOMPONENT_MULTIOPTIONDROPDOWN } from "constants/FnolComponentConstants";

export const sendOnPropertyChangeRequest = async (fieldPath, fnolInstance, dispatch, getState, options = {}) => {
  try {
    const url = await ApiLocations.getOnFnolPropertyChange();
    const requestBody = {
      property: fieldPath,
      fnolJson: fnolInstance,
    };
    var jsonRequestBody = JSON.stringify(requestBody); // This can sometimes fail in VS code due to a compiler bug. If this occurs, Debug & Run this is Google Dev Tools / Chrome instead.
    const response = await apiPost(url, jsonRequestBody);

    const changeDetails = extractChanges(fieldPath, response, options);
    const autoPopulatedByData = await getOnPropertyChangeTriggeredByDataAsync();

    const payload = getChangesForTopLevelFields(changeDetails, autoPopulatedByData).map((change) => {
      return {
        fieldPath: change.changeFieldDetails.fieldPath,
        value: change.changeFieldDetails.value,
        triggeredByFieldPath: change.changeTriggeredByDetails.fieldPath,
      };
    });
    const tpPayload = getChangesForTPFields(changeDetails, autoPopulatedByData).map((change) => {
      return {
        tpId: change.tpId,
        tpField: change.tpField,
        value: change.changeFieldDetails.value,
        triggeredByFieldPath: change.changeTriggeredByDetails.fieldPath,
      };
    });

    dispatch(updateOnPropertyChangeData(payload));
    dispatch(updateTPOnPropertyChangeData(tpPayload));
  } catch (ex) {
    toast(
      <ErrorToastContent
        errorId={ex.errorId}
        title="Couldn't save your FNOL. Please re-try again or contact IT Support."
        exception={ex.exception}
      />,
      {
        position: "top-center",
        className: "customToastError",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
      }
    );
  }
};

const getChangesForTopLevelFields = (changes, autoPopulatedByData) => {
  const relevantChanges = [];
  const autoPopulatedByDataKvp = Object.entries(autoPopulatedByData).filter(
    (kvp) => !kvp[0].includes(FieldMappingConstants.ThirdParties)
  );

  for (const change of changes) {
    if (
      isChangeForStandardTopLevelField(change) &&
      change.changeFieldDetails.fieldPath !== fnolOnPropertyChangeConstants.ResetPropagator
    ) {
      relevantChanges.push(change);
      // should only ever be one empty change in changes passed in
    } else if (change.changeFieldDetails.fieldPath === fnolOnPropertyChangeConstants.ResetPropagator) {
      const possibleFieldsToReset = autoPopulatedByDataKvp.filter((kvp) =>
        change.shouldFieldBeResetByThisChange(new TriggeredByDetails(kvp[1]))
      );
      for (let [key] of possibleFieldsToReset) {
        if (key.includes("%(")) {
          key = key.slice(0, key.indexOf("%("));
        }
        if (!relevantChanges.some((changeObj) => changeObj.doesChangeAssignField(key))) {
          relevantChanges.push(change.generateResetChangeForField(key));
        }
      }
    }
  }

  return relevantChanges;
};

const getChangesForTPFields = (changes, autoPopulatedByData) => {
  const relevantChanges = [];
  // const autoPopulatedByDataKvp = Object.entries(autoPopulatedByData).filter((kvp) =>
  //   kvp[0].includes(FieldMappingConstants.ThirdParties)
  // );

  for (const change of changes) {
    if (
      change.changeFieldDetails.fieldPath.includes(FieldMappingConstants.ThirdParties) &&
      change.changeFieldDetails.fieldPath !== fnolOnPropertyChangeConstants.ResetPropagator
    ) {
      relevantChanges.push(change);
    }
    // commenting out for now but will be needed once the tp stuff is implemented. MB.

    // else if (change.changeFieldDetails.fieldPath === fnolOnPropertyChangeConstants.EmptyChange) {
    //   const possibleFieldsToReset = autoPopulatedByDataKvp.filter((kvp) =>
    //     change.shouldFieldBeResetByThisChange(new TriggeredByDetails(kvp[1]))
    //   );
    // for (const [key] of possibleFieldsToReset) {
    //   if (!relevantChanges.some((changeObj) => changeObj.doesChangeAssignField(key))) {
    //     relevantChanges.push(change.generateResetChangeForField(key));
    //   }
    // }
    //}
  }

  return relevantChanges;
};

const isChangeForStandardTopLevelField = (change) => {
  return !change.changeFieldDetails.fieldPath.includes(FieldMappingConstants.ThirdParties);
};

const extractChanges = (fieldPath, response, options) => {
  switch (options.controlType) {
    case FNOLCOMPONENT_MULTIOPTIONDROPDOWN:
      return extractChangesFromMultiDropdownTrigger(response, fieldPath, options);
    default:
      const changes = new OnPropertyChangeResponse(response);
      const triggeredBy = new TriggeredByDetails(fieldPath);
      const changeDetails = changes.changes.map((change) => new ChangeDetails(change, triggeredBy));
      // facilitates the resetting mechanism
      const resetPropagator = new ChangeDetails(
        new ChangeFieldDetails(fnolOnPropertyChangeConstants.ResetPropagator, null),
        triggeredBy
      );
      changeDetails.push(resetPropagator);
      return changeDetails;
  }
};

const extractChangesFromMultiDropdownTrigger = (response, fieldPath, options) => {
  const changes = new MultiDropdownOnPropertyChangeResponse(response);
  const changeDetails = changes.changes.map((change) => {
    const triggeredBy = new TriggeredByDetails(
      generateAutoPopulatedByStringForMultiDropdownField(fieldPath, change.triggerOption)
    );
    return new ChangeDetails(new ChangeFieldDetails(change.fieldPath, change.value), triggeredBy);
  });

  for (const option of options.removedOptions ?? []) {
    const triggeredBy = new TriggeredByDetails(
      generateAutoPopulatedByStringForMultiDropdownField(fieldPath, option.value)
    );
    changeDetails.push(
      new ChangeDetails(new ChangeFieldDetails(fnolOnPropertyChangeConstants.ResetPropagator, null), triggeredBy)
    );
  }

  return changeDetails;
};
